import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef, ModuleRegistry, ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, DateEditorModule, RowAutoHeightModule, GridReadyEvent, GridApi, DateFilterModule, TextFilterModule, NumberFilterModule, ColumnApiModule, ClientSideRowModelApiModule, CellApiModule, ColumnResizedEvent } from 'ag-grid-community';
import { Box, Paper, useTheme, Popover, TextField, InputAdornment, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, IconButton, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { Search as SearchIcon, FileDownload as FileDownloadIcon, Add as AddIcon, Close as CloseIcon, ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { MDRData, WorkflowStep, Party, RevisionName, Project } from '../types';
import { StatusChip, WorkflowSteps, InlineWorkflowEditor } from './MDRComponents';
import * as XLSX from 'xlsx';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { localStore } from '../utils/localStore';

// Register AG Grid modules
ModuleRegistry.registerModules([ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, DateEditorModule, RowAutoHeightModule, DateFilterModule, TextFilterModule, NumberFilterModule, ColumnApiModule, ClientSideRowModelApiModule, CellApiModule]);

// Import AG Grid styles
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

interface MDRTableReactDataGridProps {
    data: MDRData[];
    projects: Project[];
    revisionNames: RevisionName[];
    parties: Party[];
    onUpdateDate: (docId: number, revId: number, date: string | null) => Promise<void>;

    onAddRevision: (docId: number, data: any) => Promise<{ revision_id: number } | void>;
    onRowClick?: (data: MDRData) => void;
    onRowDoubleClick?: (data: MDRData) => void;
    showProjectFilter?: boolean;
    columnWidthsStorageKey?: string;
}

interface GridRow extends MDRData {
    [key: string]: any;
}

const MDRTableReactDataGrid: React.FC<MDRTableReactDataGridProps> = ({
    data,
    projects,
    revisionNames,
    parties,
    onUpdateDate,

    onAddRevision,
    onRowClick,
    onRowDoubleClick,
    showProjectFilter = true,
    columnWidthsStorageKey = 'mdr_table_column_widths_v1',
}) => {
    const theme = useTheme();
    const [quickFilter, setQuickFilter] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState<number | 'all'>('all');

    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [colVisAnchorEl, setColVisAnchorEl] = useState<HTMLElement | null>(null);
    const [allColumns, setAllColumns] = useState<any[]>([]);

    // Saved widths are kept in a ref so we can re-apply them whenever
    // columnDefs change (the revision columns are rebuilt from data).
    const savedColumnWidthsRef = useRef<Record<string, number> | null>(null);
    const savedWidthsLoadedRef = useRef(false);
    const [savedWidthsVersion, setSavedWidthsVersion] = useState(0);

    const applySavedColumnWidthsToApi = useCallback((api: GridApi) => {
        const saved = savedColumnWidthsRef.current;
        if (!saved) return;

        const state = Object.entries(saved)
            .filter(([, width]) => typeof width === 'number' && width > 0)
            // flex: null ensures the saved width is not overridden by any
            // column-level flex layout calculation that AG Grid runs later.
            .map(([colId, width]) => ({ colId, width, flex: null }));

        if (state.length) {
            api.applyColumnState({ state, applyOrder: false });
        }
    }, []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        void (async () => {
            try {
                const savedColumnWidths = await localStore.get(columnWidthsStorageKey) as Record<string, number> | undefined;
                if (savedColumnWidths && typeof savedColumnWidths === 'object') {
                    savedColumnWidthsRef.current = savedColumnWidths;
                    applySavedColumnWidthsToApi(params.api);
                }
            } catch {
                // Ignore storage failures; table remains functional.
            } finally {
                savedWidthsLoadedRef.current = true;
                setSavedWidthsVersion((v) => v + 1);
            }
        })();
    }, [columnWidthsStorageKey, applySavedColumnWidthsToApi]);

    const handleColumnResized = useCallback(async (event: ColumnResizedEvent) => {
        if (!event.finished || event.source === 'api') {
            return;
        }

        try {
            const widthsByColumnId = event.api.getColumnState().reduce<Record<string, number>>((acc, columnState) => {
                if (columnState.colId && typeof columnState.width === 'number' && columnState.width > 0) {
                    acc[columnState.colId] = columnState.width;
                }
                return acc;
            }, {});

            if (Object.keys(widthsByColumnId).length) {
                savedColumnWidthsRef.current = widthsByColumnId;
                await localStore.set(columnWidthsStorageKey, widthsByColumnId);
            }
        } catch {
            // Ignore storage failures; table remains functional.
        }
    }, [columnWidthsStorageKey]);

    const toggleColumn = (colId: string, visible: boolean) => {
        if (gridApi) {
            gridApi.setColumnsVisible([colId], visible);
            setAllColumns(prev => prev.map(c =>
                c.colId === colId ? { ...c, visible } : c
            ));
        }
    };

    // Add Revision State
    const [addRevDialogOpen, setAddRevDialogOpen] = useState(false);
    const [addRevDocId, setAddRevDocId] = useState<number | null>(null);
    const [newRevNameId, setNewRevNameId] = useState<number | ''>('');
    const [newRevStep, setNewRevStep] = useState('');
    const [newRevDate, setNewRevDate] = useState('');
    const [newRevWorkflow, setNewRevWorkflow] = useState<WorkflowStep[]>([]);
    const [newRevWorkflowAnchor, setNewRevWorkflowAnchor] = useState<HTMLElement | null>(null);



    const openAddRevisionDialog = (docId: number) => {
        setAddRevDocId(docId);
        setNewRevNameId('');
        setNewRevStep('');
        setNewRevDate('');
        setNewRevWorkflow([]);
        setAddRevDialogOpen(true);
    };

    const handleAddRevisionSave = async () => {
        if (addRevDocId && newRevNameId && newRevStep) {
            await onAddRevision(addRevDocId, {
                revision_name_id: newRevNameId,
                step: parseInt(newRevStep),
                expected_issue_date: newRevDate || undefined,
                workflow: newRevWorkflow
            });
            setAddRevDialogOpen(false);
        }
    };

    // Filter data based on quick filter and project selection
    const filteredData = useMemo(() => {
        let processedData = [...data];

        // Filter by project first
        if (selectedProjectId !== 'all') {
            processedData = processedData.filter(doc => doc.project_id === selectedProjectId);
        }

        if (quickFilter) {
            const lowerFilter = quickFilter.toLowerCase();
            processedData = processedData.filter(doc => {
                // Check if document matches
                const docMatches = (
                    (doc.document_nd_number?.toLowerCase().includes(lowerFilter)) ||
                    (doc.document_title?.toLowerCase().includes(lowerFilter)) ||
                    (doc.document_type_name?.toLowerCase().includes(lowerFilter)) ||
                    (doc.work_package_code?.toLowerCase().includes(lowerFilter)) ||
                    (doc.work_unit_code?.toLowerCase().includes(lowerFilter))
                );

                if (docMatches) return true;

                // Check if any revision matches
                return doc.revisions.some(rev =>
                    (rev.revision_name_step?.toLowerCase().includes(lowerFilter)) ||
                    (rev.status?.toLowerCase().includes(lowerFilter)) ||
                    (rev.expected_issue_date?.toLowerCase().includes(lowerFilter)) ||
                    (rev.issued_date?.toLowerCase().includes(lowerFilter)) ||
                    (rev.workflow?.some(step =>
                        step.party_name?.toLowerCase().includes(lowerFilter) ||
                        step.role?.toLowerCase().includes(lowerFilter)
                    ))
                );
            });
        }

        // Sort by Work Package then Work Unit
        return processedData.sort((a, b) => {
            const wpA = a.work_package_code || '';
            const wpB = b.work_package_code || '';
            if (wpA !== wpB) return wpA.localeCompare(wpB);

            const wuA = a.work_unit_code || '';
            const wuB = b.work_unit_code || '';
            return wuA.localeCompare(wuB);
        });
    }, [data, quickFilter, selectedProjectId]);

    const dateFilterParams = useMemo(() => ({
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (cellValue == null) return -1;
            const dateParts = cellValue.split('-');
            if (dateParts.length === 3) {
                const year = Number(dateParts[0]);
                const month = Number(dateParts[1]) - 1;
                const day = Number(dateParts[2]);
                const cellDate = new Date(year, month, day);
                cellDate.setHours(0, 0, 0, 0);

                if (cellDate.getTime() === filterLocalDateAtMidnight.getTime()) {
                    return 0;
                }
                if (cellDate < filterLocalDateAtMidnight) {
                    return -1;
                }
                if (cellDate > filterLocalDateAtMidnight) {
                    return 1;
                }
            }
            return 0;
        },
        browserDatePicker: true,
        minValidYear: 2000,
        maxValidYear: 2100,
    }), []);

    // Calculate unique revisions present in the data
    const uniqueRevisions = useMemo(() => {
        const revSet = new Set<string>();
        // Use 'data' instead of 'filteredData' to keep columns stable regardless of filtering
        // Or use 'filteredData' if we want columns to disappear when filtered out.
        // User said "by default", and usually stable columns are better.
        // But "add the columns of the revisions that exists" implies present data.
        // Let's use 'data' (all loaded documents) to determine the columns structure.
        data.forEach(doc => {
            doc.revisions.forEach(rev => {
                revSet.add(rev.revision_name_step);
            });
        });

        // Sort revisions: First by revision name order (from revisionNames), then by step number
        return Array.from(revSet).sort((a, b) => {
            // Extract name part (e.g. "IFA" from "IFA 01")
            const nameA = a.split(' ')[0];
            const nameB = b.split(' ')[0];

            if (nameA !== nameB) {
                const idxA = revisionNames.findIndex(rn => rn.name === nameA);
                const idxB = revisionNames.findIndex(rn => rn.name === nameB);
                // If both found in revisionNames, sort by index
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                // If one not found, put it at the end
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
            }

            // If names are same (or both unknown), sort by full string (which handles step number naturally for "Name 01")
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });
    }, [data, revisionNames]);

    // Prepare row data with pivoted revisions
    const rowData = useMemo<GridRow[]>(() => {
        return filteredData.map(doc => {
            const row: GridRow = { ...doc };

            // Pivot revisions
            doc.revisions.forEach(rev => {
                // Use the exact revision name step as key
                // We need to sanitize the key to be safe for object properties if it contains spaces or special chars
                // But for now, let's assume revision_name_step is safe enough or use bracket access
                row[`rev_${rev.revision_name_step}`] = rev;
            });

            return row;
        });
    }, [filteredData]);

    const columnDefs = useMemo<(ColDef<GridRow> | ColGroupDef<GridRow>)[]>(() => {
        const cols: (ColDef<GridRow> | ColGroupDef<GridRow>)[] = [
            { colId: 'wp', headerName: "WP", field: "work_package_code", width: 90, pinned: 'left' },
            { colId: 'wu', headerName: "WU", field: "work_unit_code", width: 90, pinned: 'left' },
            {
                colId: 'nd_number',
                headerName: "ND Number",
                field: "document_nd_number",
                minWidth: 150,
                flex: 1,
                pinned: 'left',
                cellRenderer: (params: any) => <strong>{params.value}</strong>
            },
            { colId: 'title', headerName: "Title", field: "document_title", width: 220 },
            { colId: 'type', headerName: "Type", field: "document_type_name", width: 100 },
            {
                colId: 'actions',
                headerName: "Actions",
                width: 80,
                filter: false,
                cellRenderer: (params: any) => (
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openAddRevisionDialog(params.data.document_id)}
                        title="Add Revision"
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                )
            }
        ];

        // Add columns for each unique revision found
        uniqueRevisions.forEach(revNameStep => {
            cols.push({
                headerName: revNameStep,
                children: [
                    {
                        colId: `status_${revNameStep}`,
                        headerName: "Status",
                        width: 120,
                        valueGetter: (params: any) => params.data[`rev_${revNameStep}`]?.status,
                        cellRenderer: (params: any) => {
                            if (params.value) {
                                return <StatusChip status={params.value} />;
                            }
                            return null;
                        }
                    },
                    {
                        colId: `exp_date_${revNameStep}`,
                        headerName: "Exp. Date",
                        width: 120,
                        valueGetter: (params: any) => params.data[`rev_${revNameStep}`]?.expected_issue_date,
                        valueFormatter: (params: any) => {
                            if (params.value) return new Date(params.value).toLocaleDateString();
                            return '';
                        },
                        editable: (params: any) => !!params.data[`rev_${revNameStep}`],
                        cellEditor: 'agDateCellEditor',
                        filter: 'agDateColumnFilter',
                        filterParams: dateFilterParams,
                        floatingFilter: true,
                        onCellValueChanged: (params: any) => {
                            const rev = params.data[`rev_${revNameStep}`];
                            if (rev && params.newValue !== params.oldValue) {
                                let dateStr = params.newValue;
                                if (params.newValue instanceof Date) {
                                    dateStr = params.newValue.toISOString().split('T')[0];
                                }
                                onUpdateDate(params.data.document_id, rev.revision_id, dateStr);
                            }
                        }
                    },
                    {
                        colId: `issued_date_${revNameStep}`,
                        headerName: "Issued Date",
                        width: 120,
                        valueGetter: (params: any) => params.data[`rev_${revNameStep}`]?.issued_date,
                        valueFormatter: (params: any) => {
                            if (params.value) return new Date(params.value).toLocaleDateString();
                            return '';
                        },
                        filter: 'agDateColumnFilter',
                        filterParams: dateFilterParams,
                        floatingFilter: true
                    },
                    {
                        colId: `workflow_${revNameStep}`,
                        headerName: "Workflow",
                        width: 200,
                        filter: false,
                        valueGetter: (params: any) => params.data[`rev_${revNameStep}`]?.workflow,
                        cellRenderer: (params: any) => {
                            const rev = params.data[`rev_${revNameStep}`];
                            if (rev && rev.workflow) {
                                return (
                                    <WorkflowSteps
                                        workflow={rev.workflow}
                                    />
                                );
                            }
                            return null;
                        }
                    }
                ]
            });
        });

        return cols;
    }, [uniqueRevisions, onUpdateDate, dateFilterParams]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true
    }), []);

    // Re-apply saved widths whenever columnDefs change (revision columns are
    // rebuilt from data), because AG Grid resets widths to those declared in
    // the freshly-passed columnDefs.
    useEffect(() => {
        if (gridApi && savedWidthsLoadedRef.current) {
            applySavedColumnWidthsToApi(gridApi);
        }
    }, [gridApi, columnDefs, applySavedColumnWidthsToApi, savedWidthsVersion]);

    const onExport = useCallback(() => {
        if (!gridApi) return;

        const headerRow1: any[] = [];
        const headerRow2: any[] = [];
        const merges: any[] = [];
        const colIds: string[] = [];
        let colIndex = 0;

        // Helper to find column by ID
        const allGridCols = gridApi.getAllGridColumns();
        const getGridCol = (id: string) => allGridCols.find(c => c.getColId() === id);

        // 1. Construct Headers based on columnDefs and current visibility
        columnDefs.forEach((col: any) => {
            if (col.colId === 'actions') return;

            if ('children' in col) {
                // Group Column (Revisions)
                const visibleChildren = col.children.filter((child: any) => {
                    const gridCol = getGridCol(child.colId);
                    return gridCol && gridCol.isVisible();
                });

                if (visibleChildren.length > 0) {
                    // Add Group Header
                    headerRow1.push(col.headerName);
                    // Fill placeholders for the span
                    for (let i = 1; i < visibleChildren.length; i++) {
                        headerRow1.push('');
                    }
                    // Merge Group Header
                    merges.push({ s: { r: 0, c: colIndex }, e: { r: 0, c: colIndex + visibleChildren.length - 1 } });

                    // Add Children Headers
                    visibleChildren.forEach((child: any) => {
                        headerRow2.push(child.headerName);
                        colIds.push(child.colId);
                    });

                    colIndex += visibleChildren.length;
                }
            } else {
                // Leaf Column (Static)
                const gridCol = getGridCol(col.colId);
                if (gridCol && gridCol.isVisible()) {
                    headerRow1.push(col.headerName);
                    headerRow2.push(''); // Placeholder for vertical merge

                    // Merge Vertically (Row 1 to Row 2)
                    merges.push({ s: { r: 0, c: colIndex }, e: { r: 1, c: colIndex } });

                    colIds.push(col.colId);
                    colIndex++;
                }
            }
        });

        if (colIds.length === 0) {
            console.warn("No visible columns found for export.");
            return;
        }

        // 2. Get Data Rows respecting filters/sorting
        const dataRows: any[][] = [];
        gridApi.forEachNodeAfterFilterAndSort((node) => {
            const row: any[] = [];
            colIds.forEach(colId => {
                let val = gridApi.getCellValue({ rowNode: node, colKey: colId });

                // Format dates if needed
                if (val instanceof Date) {
                    val = val.toISOString().split('T')[0];
                }

                // Handle workflow array to string
                if (Array.isArray(val)) {
                    val = val.map(step => `${step.role}: ${step.party_name}`).join(', ');
                }

                if (val === null || val === undefined) val = '';
                row.push(val);
            });
            dataRows.push(row);
        });

        // 3. Create Worksheet
        const wsData = [headerRow1, headerRow2, ...dataRows];
        const worksheet = XLSX.utils.aoa_to_sheet(wsData);

        // Apply Merges
        if (merges.length > 0) {
            worksheet['!merges'] = merges;
        }

        // Set Column Widths
        const wscols = colIds.map(() => ({ wch: 15 }));
        worksheet['!cols'] = wscols;

        // 4. Write File
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MDR Export');
        XLSX.writeFile(workbook, `mdr_export_${new Date().toISOString().split('T')[0]}.xlsx`);

    }, [gridApi, columnDefs]);

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
                {showProjectFilter && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Project</InputLabel>
                        <Select
                            value={selectedProjectId}
                            label="Project"
                            onChange={(e) => setSelectedProjectId(e.target.value as number | 'all')}
                            MenuProps={getZoomCompensatedSelectMenuProps()}
                        >
                            <MenuItem value="all">All Projects</MenuItem>
                            {projects.map(project => (
                                <MenuItem key={project.id} value={project.id}>
                                    {project.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    size="small"
                    placeholder="Quick filter..."
                    value={quickFilter}
                    onChange={(e) => setQuickFilter(e.target.value)}
                    sx={{ flex: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    variant="outlined"
                    startIcon={<ViewColumnIcon />}
                    onClick={(e) => {
                        setColVisAnchorEl(e.currentTarget);
                        if (gridApi) {
                            const cols = gridApi.getAllGridColumns();
                            if (cols) {
                                setAllColumns(cols.map(c => ({
                                    colId: c.getColId(),
                                    headerName: c.getColDef().headerName || c.getColId(),
                                    visible: c.isVisible()
                                })));
                            }
                        }
                    }}
                >
                    Columns
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={onExport}
                >
                    Export XLSX
                </Button>
            </Stack>

            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
                className={theme.palette.mode === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
            >
                <AgGridReact
                    theme="legacy"
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.document_id.toString()}
                    rowHeight={45}
                    onGridReady={onGridReady}
                    onColumnResized={(event) => {
                        void handleColumnResized(event);
                    }}
                    onRowClicked={(event) => onRowClick && onRowClick(event.data)}
                    onRowDoubleClicked={(event) => onRowDoubleClick && onRowDoubleClick(event.data)}
                />
            </Paper>

            <Popover
                open={Boolean(colVisAnchorEl)}
                anchorEl={colVisAnchorEl}
                onClose={() => setColVisAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto', minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Show/Hide Columns</Typography>
                    <FormGroup>
                        {allColumns.map(col => (
                            <FormControlLabel
                                key={col.colId}
                                control={
                                    <Checkbox
                                        checked={col.visible}
                                        onChange={(e) => toggleColumn(col.colId, e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={col.headerName}
                            />
                        ))}
                    </FormGroup>
                </Box>
            </Popover>



            {/* Add Revision Dialog */}
            < Dialog open={addRevDialogOpen} onClose={() => setAddRevDialogOpen(false)} maxWidth="sm" fullWidth >
                <DialogTitle>Add New Revision</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Revision Name</InputLabel>
                                <Select
                                    value={newRevNameId}
                                    label="Revision Name"
                                    onChange={(e) => setNewRevNameId(Number(e.target.value))}
                                    MenuProps={getZoomCompensatedSelectMenuProps()}
                                >
                                    {revisionNames.map(r => (
                                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Step"
                                type="number"
                                value={newRevStep}
                                onChange={(e) => setNewRevStep(e.target.value)}
                                sx={{ width: 100 }}
                            />
                        </Stack>
                        <TextField
                            size="small"
                            label="Expected Issue Date"
                            type="date"
                            value={newRevDate}
                            onChange={(e) => setNewRevDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <Box>
                            <Button
                                variant="outlined"
                                onClick={(e) => setNewRevWorkflowAnchor(e.currentTarget)}
                            >
                                {newRevWorkflow.length > 0 ? `${newRevWorkflow.length} Steps Configured` : 'Configure Workflow'}
                            </Button>
                            <Popover
                                open={Boolean(newRevWorkflowAnchor)}
                                anchorEl={newRevWorkflowAnchor}
                                onClose={() => setNewRevWorkflowAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                <InlineWorkflowEditor
                                    initialWorkflow={newRevWorkflow}
                                    parties={parties}
                                    onSave={(wf) => {
                                        setNewRevWorkflow(wf);
                                        setNewRevWorkflowAnchor(null);
                                    }}
                                    onCancel={() => setNewRevWorkflowAnchor(null)}
                                />
                            </Popover>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRevDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddRevisionSave} variant="contained" disabled={!newRevNameId || !newRevStep}>
                        Create Revision
                    </Button>
                </DialogActions>
            </Dialog >
        </Box >
    );
};

export default MDRTableReactDataGrid;
